import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import Layout from './Layout';
import useDocumentTitle from '../hooks/useDocumentTitle';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function BlogPost() {
  const { slug } = useParams();
  const [content, setContent] = useState('');
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const base = process.env.PUBLIC_URL;

    fetch(`${base}/blogs/manifest.json`)
      .then(res => res.json())
      .then(data => {
        const post = data.find(p => p.slug === slug);
        if (post) setMeta(post);
      })
      .catch(() => {});

    fetch(`${base}/blogs/${slug}/content.json`)
      .then(res => {
        if (!res.ok) throw new Error('Not found');
        return res.json();
      })
      .then(data => {
        setContent(data.content);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [slug]);

  useDocumentTitle(meta ? meta.title : 'Loading...');

  const backNav = <Link to="/writings" className="back-link">&larr; Writings</Link>;

  if (loading) {
    return (
      <Layout nav={backNav}>
        <p className="status-text">Loading...</p>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout nav={backNav}>
        <p className="status-text">Post not found.</p>
      </Layout>
    );
  }

  return (
    <Layout nav={backNav}>
      <div className="post">
        {meta && (
          <header className="post-header">
            <h1 className="post-title">{meta.title}</h1>
            {meta.date && (
              <time className="post-date">{formatDate(meta.date)}</time>
            )}
          </header>
        )}

        <article className="post-body">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noreferrer">{props.children}</a>
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        </article>

        <footer className="post-footer">
          <Link to="/writings" className="back-link">&larr; Writings</Link>
        </footer>
      </div>
    </Layout>
  );
}

export default BlogPost;
